import { FC, useState } from 'react';

import videoPlayHoverBtn from 'assets/images/video-play-button-hover.svg';
import videoPlayBtn from 'assets/images/video-play-button.svg';

import Icon from 'atoms/Icons/Icon';
import color from 'atoms/colors/colors';

// Quarks
import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Image from 'quarks/Image';
import Link from 'quarks/Link';
import Text from 'quarks/Text';

import ComponentButton from 'components/Button/ComponentButton';
import VideoPopup from 'components/Video/VideoPopup';

import richTextParser from 'utils/richTextParser';

import type { ContentfulResourceCard } from 'graphqlTypes';

const textWrapCSS = `
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const headingWrapCSS = `
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const StandardCard: FC<ContentfulResourceCard> = ({
  body,
  heading,
  featuredImage,
  backgroundIsDark,
  pageType,
  youTubeUrl,
  link,
  ...props
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [imageHover, setImageHover] = useState(false);

  return (
    <>
      <Link
        href={youTubeUrl ? null : link}
        onClick={() => setModalOpen(true)}
        textDecoration="none"
        height="100%"
        width="100%"
      >
        <Flex
          width="100%"
          height="100%"
          flexDirection="column"
          borderRadius="12px"
          overflowX="hidden"
          overflowY="hidden"
          border="1px solid"
          borderColor="gray-200"
          hover={{ boxShadow: 'xxl' }}
          transition="200ms ease-in-out"
          backgroundColor={backgroundIsDark ? 'gray-800' : 'common-white'}
          css={`
            button,
            a {
              padding-left: 0;
            }

            &:hover {
              cursor: pointer;
            }
          `}
          onMouseEnter={() => setImageHover(true)}
          onMouseLeave={() => setImageHover(false)}
          {...props}
        >
          <Flex
            position="relative"
            css={`
              .play-button {
                inset: 0;
                margin: auto;
                position: absolute;
                width: 70px;
              }

              img {
                max-height: 184px;
                width: 100%;
                object-fit: cover;
              }
            `}
          >
            <Image
              image={(featuredImage?.bynderImageDesktop || featuredImage?.imageDesktop) as ImageData}
              alt={`${pageType} Card Image`}
              objectFit="cover"
              objectPosition="center"
              minHeight="184px"
              maxHeight="184px"
              width="100%"
            />
            {youTubeUrl && (
              <img src={imageHover ? videoPlayHoverBtn : videoPlayBtn} alt="video-play-btn" className="play-button" />
            )}
          </Flex>
          <Flex
            position="relative"
            flexDirection="column"
            justifyContent="space-between"
            gap="16px"
            height="100%"
            maxHeight="fit-content"
            sm={{ maxHeight: 'calc(100% - 208px)' }}
            css={`
              padding: 2rem 2rem 5rem 2rem;
            `}
          >
            <Flex flexDirection="column" gap="16px">
              {heading && (
                <Heading
                  as="h4"
                  fontSize="textLg"
                  lineHeight="textLg"
                  fontWeight="semiBold"
                  data-cy="title"
                  textColor={backgroundIsDark ? 'common-white' : 'gray-900'}
                  css={headingWrapCSS}
                >
                  {heading}
                </Heading>
              )}
              {body && (
                <Text textStyle="textSm" textColor={backgroundIsDark ? 'gray-300' : 'gray-900'} css={textWrapCSS}>
                  {richTextParser(body)}
                </Text>
              )}
            </Flex>
            <Flex gap="4px" alignItems="center" position="absolute" xs={{ bottom: '20px' }} md={{ bottom: '0px' }}>
              <ComponentButton link={link} label="Learn more" buttonColor="Gray 900 Link" />
              <Icon id="chevron-right" fill={color.gray[900]} size={20} />
            </Flex>
          </Flex>
        </Flex>
      </Link>
      <VideoPopup open={modalOpen} onClose={() => setModalOpen(false)} videoUrl={youTubeUrl} />
    </>
  );
};

export default StandardCard;
